import clsx from "clsx";
import { Link, NavLink } from "react-router-dom";
import { Avatar, Menu } from "@mantine/core";

// Images
import emptyUser from "@assets/blank-user.webp";
import { useAppDispatch, useAppSelector } from "@src/app/hooks";
import WarningDialog from "@/src/components/WarningDialog/WarningDialog";
import { Fragment, useState } from "react";
import api from "@/src/utils";
import { useMutation } from "@tanstack/react-query";
import { signOut } from "@/src/features/userSlice";
import { notification } from "antd";
import Notifications from "../Notifications";
import ThemeSwitcher from "@/src/components/ThemeSwitcher/ThemeSwitcher";
import { textVariants } from "@/src/components/Text/Text";
import apiRoutes from "@/src/apiRoutes";
import { useTheme } from "@/src/context/themeContext";
import { cn } from "@/lib/utils";
import { ChevronDown, MenuIcon, XIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/src/components/shad/dropdown";
import receiveIcon from "@assets/icons/money-recive.svg";
import sendIcon from "@assets/icons/money-send.svg";
import convertIcon from "@assets/icons/bitcoin-convert.svg";
import Logo from "@/src/svg/logo";
import { useMediaQuery } from "usehooks-ts";
import { Dialog, Transition } from "@headlessui/react";
import SmallLogo from "@/src/svg/smallLogo";
import { useTranslation } from "react-i18next";

const Navbar = () => {
  const { theme } = useTheme();
  const [openSidebar, setOpenSidebar] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [openWallet, setOpenWallet] = useState(false);
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.navbar.${key}`);

  const routes = [
    {
      name: t("app.navbar.home"),
      route: "/app/home",
    },
    {
      name: t("app.navbar.buy"),
      route: "/app/trade/buy",
    },
    {
      name: t("app.navbar.sell"),
      route: "/app/trade/sell",
    },
    {
      name: t("app.navbar.p2p"),
      route: "/app/trade/p2p",
    },
    {
      name: t("app.navbar.payments"),
      route: "/app/payments",
    },
    {
      name: t("app.navbar.referral"),
      route: "/app/earn",
    },
    {
      name: t("app.navbar.settings"),
      route: "/app/settings/profile",
    },
  ];

  return (
    <div className="md:px-4 pt-4 grid gap-2 fixed w-full z-20 dark:bg-grey-800 bg-white  h-16 top-0  shadow-sm shadow-gray-200 dark:shadow-grey-700">
      <div className="flex justify-between px-2 ">
        <div className="flex items-center gap-14">
          {isMobile ? (
            <MenuIcon
              className="h-6 w-6 dark:text-white"
              onClick={() => setOpenSidebar(true)}
            />
          ) : (
            <Logo
              height={"30px"}
              width={"150px"}
              color={theme === "dark" ? "white" : undefined}
            />
          )}
          <div className="md:flex hidden md:space-x-5  overflow-x-scroll">
            {routes?.map((item) => (
              <NavLink
                key={item?.name}
                className={({ isActive }) =>
                  clsx(
                    isActive &&
                      "border-b-2 border-indigo dark:border-grey-300 text-indigo dark:text-grey-200 ",
                    textVariants(),
                    " text-sm text-gray-700 md:pb-1 whitespace-nowrap flex font-semibold "
                  )
                }
                to={item?.route}
              >
                <span className="hover:bg-indigo-200 dark:hover:bg-grey-500 p-2  rounded-md">
                  {item?.name}
                </span>
              </NavLink>
            ))}
          </div>
        </div>

        <div className="flex space-x-5 items-center ">
          <DropdownMenu open={openWallet} onOpenChange={setOpenWallet}>
            <DropdownMenuTrigger>
              <span className="flex items-center gap-1 cursor-pointer">
                <span
                  className={cn(textVariants({ size: "sm", font: "semibold" }))}
                >
                  {trans("wallet.label")}
                </span>
                <ChevronDown className="h-4 w-4 dark:text-white" />
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent className=" w-[200px]">
              <DropdownMenuItem onClick={() => setOpenWallet(false)}>
                <Link to={"/app/wallet"} className="flex items-center gap-1">
                  <span className={cn(textVariants({ variant: "desc" }))}>
                    {trans("wallet.dropdown.overview")}
                  </span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setOpenWallet(false)}>
                <Link
                  to={"/app/wallet/trading"}
                  className="flex items-center gap-1"
                >
                  <span className={cn(textVariants({ variant: "desc" }))}>
                    {trans("wallet.dropdown.trading")}
                  </span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setOpenWallet(false)}>
                <Link
                  to={"/app/wallet/history"}
                  className="flex items-center gap-1"
                >
                  <span className={cn(textVariants({ variant: "desc" }))}>
                    {trans("wallet.dropdown.history")}
                  </span>
                </Link>
              </DropdownMenuItem>
              {/* <DropdownMenuItem onClick={() => setOpenWallet(false)}>
                <Link
                  to={"/app/trade/history"}
                  className="flex items-center gap-1"
                >
                  <span className={cn(textVariants({ variant: "desc" }))}>
                    History
                  </span>
                </Link>
              </DropdownMenuItem> */}
              <DropdownMenuGroup className="px-2 grid gap-3 my-4">
                <Link
                  to={"/app/wallet/funding"}
                  onClick={() => setOpenWallet(false)}
                  className="flex items-center gap-2 border border-gray-200 dark:border-grey-500 rounded-md py-1 px-3"
                >
                  <img src={receiveIcon} className="h-5 w-5" alt="receive" />
                  <span>{trans("wallet.dropdown.deposit")}</span>
                </Link>
                <Link
                  to={"/app/wallet/funding/withdraw"}
                  onClick={() => setOpenWallet(false)}
                  className="flex items-center gap-2 border border-gray-200 dark:border-grey-500 rounded-md py-1 px-3"
                >
                  <img src={sendIcon} className="h-5 w-5" alt="send" />
                  <span>{trans("wallet.dropdown.withdraw")}</span>
                </Link>
                <Link
                  to={"/app/wallet/funding/convert"}
                  onClick={() => setOpenWallet(false)}
                  className="flex items-center gap-2 border border-gray-200 dark:border-grey-500 rounded-md py-1 px-3"
                >
                  <img src={convertIcon} className="h-5 w-5" alt="receive" />
                  <span>{trans("wallet.dropdown.convert")}</span>
                </Link>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          <ThemeSwitcher />
          <MenuItem />
          <Notifications />
        </div>
      </div>
      <Transition.Root show={openSidebar} as={Fragment}>
        <Dialog
          as="div"
          id="sidebar"
          className="fixed md:hidden z-[100]"
          onClose={setOpenSidebar}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setOpenSidebar(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white dark:bg-grey-700 px-6 pb-4">
                  <div className="flex h-16 shrink-0 items-center">
                    <SmallLogo
                      height={30}
                      width={30}
                      color={theme === "dark" ? "white" : undefined}
                    />
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul role="list" className="-mx-2 space-y-3 ">
                          {routes?.map((item) => (
                            <NavLink
                              key={item?.name}
                              onClick={() => setOpenSidebar(false)}
                              className={({ isActive }) =>
                                clsx(
                                  isActive &&
                                    " dark:border-grey-300 text-indigo dark:text-grey-200",
                                  textVariants(),
                                  " text-sm text-black md:pb-1 mt-2 whitespace-nowrap flex w-full "
                                )
                              }
                              to={item?.route}
                            >
                              <span className="hover:bg-indigo-200 dark:hover:bg-indigo-500 p-2  rounded-md w-full">
                                {item?.name}
                              </span>
                            </NavLink>
                          ))}
                        </ul>
                      </li>
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default Navbar;

export const MenuItem = () => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.navbar.${key}`);
  const [open, setOpen] = useState(false);
  const { user } = useAppSelector((state) => state?.user);
  const useragent = navigator.userAgent;
  // const navigate = useNavigate();
  const dispatch = useAppDispatch();

  async function logOut() {
    const res = api.post(apiRoutes.logout, { ua: useragent });
    return (await res).data;
  }

  const { mutate, isLoading } = useMutation({
    mutationKey: ["sign_out"],
    mutationFn: logOut,
    onSuccess: (data) => {
      if (data?.success) {
        dispatch(signOut());
        window.location.assign("/");
      } else notification.warning({ message: data.info });
    },
  });

  return (
    <>
      <Menu shadow="md" width={250} position={"bottom-end"}>
        <Menu.Target>
          {/* <Button>Toggle menu</Button> */}
          {/* <img src={emptyUser} className="h-8 w-8 rounded-full" /> */}
          <Avatar
            className="cursor-pointer"
            src={user?.image ?? emptyUser}
            alt={user?.username}
            radius={"xl"}
          />
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>{user?.username}</Menu.Label>
          <Link to="/app/home">
            <Menu.Item className="text-gray-500">
              {trans("profile.dashboard")}
            </Menu.Item>
          </Link>
          <Link to="/app/settings/profile">
            <Menu.Item className="text-gray-500">
              {trans("profile.settings")}
            </Menu.Item>
          </Link>
          <Menu.Divider />
          <Menu.Item onClick={() => setOpen(true)} className="text-red-500">
            {trans("profile.logout")}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <WarningDialog
        setOpen={setOpen}
        opened={open}
        title="Are you sure?"
        description="You are logging out of your account. Click the right button to confirm your action"
        onClick={mutate}
        loading={isLoading}
      />
    </>
  );
};
